import React, { useState, useEffect } from "react";
import { Range, getTrackBackground } from "react-range";

type RangeType = { 
  MinRange: number | undefined,
  MaxRange: number | undefined,
  MinRangePercent: number | undefined,
  MaxRangePercent: number | undefined,
};

type FilterRangePickerProps = {
  isOpen: boolean;
  onClose: () => void;
  labelRange?: string;
  defaultValues?: RangeType;
  style?: React.CSSProperties;
  labelPercentageRange?: string;
  onChange: (selection: Partial<RangeType>) => void;
};

const FilterRangePicker: React.FC<FilterRangePickerProps> = ({
  isOpen,
  onClose,
  onChange,
  style = {},
  labelRange,
  defaultValues,
  labelPercentageRange,
}) => {
  const [range, setRange] = useState<RangeType>(defaultValues ?? {
    MinRange: undefined, MaxRange: undefined, MinRangePercent: 0, MaxRangePercent: 10,
  });
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);

  // Preserve state during re-renders by tracking if we're in the middle of selection
  // Only reset range when defaultValues changes, but only if not in the middle of selection
  useEffect(() => {
    if (!isSelecting) {
      if (!hasInitialized) {
        // On first render, initialize with defaultValues if provided
        if (defaultValues) {
          setRange(defaultValues);
        }
        setHasInitialized(true);
        return;
      }

      // After initialization, only reset if defaultValues is explicitly provided
      // This handles cases where parent component explicitly passes cleared values
      if (defaultValues !== undefined) {
        setRange(defaultValues);
      }
    }
  }, [defaultValues, hasInitialized, isSelecting]);

  if (!isOpen) return null;

  const handleClear = () => {
    const clearedRange: RangeType = {
      MinRange: undefined,
      MaxRange: undefined,
      MinRangePercent: 0,
      MaxRangePercent: 100,
    };
    setRange(clearedRange);
    setIsSelecting(false);
    // Clear only resets internal state, does NOT trigger filter change
    // User must click Apply to actually change the filter
  };

  const handleApply = () => {
    // Only trigger onChange when apply is clicked
    onChange(range);
    setIsSelecting(false);
    onClose();
  };

  const handleCancel = () => {
    // Reset to default values if provided, otherwise keep current selection
    if (defaultValues !== undefined) {
      setRange(defaultValues);
    }
    setIsSelecting(false);
    onClose();
  };

  return (
    <div
      style={style}
      className="filter-range-picker"
      onMouseLeave={onClose}
    >
      {/* Header with title and clear button */}
      <div className="filter-range-picker-header">
        <h3>{labelPercentageRange || "Select Range"}</h3>
        <button 
          onClick={handleClear}
          className="filter-clear-btn"
          title="Clear selection"
        >
          Clear
        </button>
      </div>

      <div className="filter-range-picker-content">
        <div className="filter-range-picker-percentage-display">
          <span>{range.MinRangePercent ?? 0}%</span>
          <span>{range.MaxRangePercent ?? 100}%</span>
        </div>
        <Range
          step={10}
          min={0}
          max={100}
          values={[range.MinRangePercent ?? 0, range.MaxRangePercent ?? 100]}
          onChange={(values) => {
            // Set selecting state to prevent external resets during user interaction
            setIsSelecting(true);
            
            // Prevent ranges from joining by stopping movement at minimum distance
            const minDistance = 10; // Minimum 10% difference
            let [minVal, maxVal] = values;
            
            // Get current values to determine which thumb is being moved
            const currentMin = range.MinRangePercent ?? 0;
            const currentMax = range.MaxRangePercent ?? 100;
            
            // Check if the movement would violate minimum distance
            if (maxVal - minVal < minDistance) {
              // Determine which thumb was moved more
              const minMoved = Math.abs(minVal - currentMin) > Math.abs(maxVal - currentMax);
              
              if (minMoved) {
                // Min thumb was moved, prevent it from going too close to max
                minVal = Math.max(0, currentMax - minDistance);
              } else {
                // Max thumb was moved, prevent it from going too close to min
                maxVal = Math.min(100, currentMin + minDistance);
              }
            }
            
            setRange((prev) => ({
              ...prev,
              MinRangePercent: minVal,
              MaxRangePercent: maxVal,
            }));
          }}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              style={{
                ...props.style,
                height: "6px",
                width: "100%",
                background: getTrackBackground({
                  values: [range.MinRangePercent ?? 0, range.MaxRangePercent ?? 100],
                  colors: ["#ccc", "#91c0ef", "#ccc"],
                  min: 0,
                  max: 100,
                }),
                borderRadius: 4,
              }}
            >
              {children}
            </div>
          )}
          renderThumb={({ props }) => (
            <div
              {...props}
              style={{
                ...props.style,
                height: "16px",
                width: "20px",
                backgroundColor: "#007AFF",
                borderRadius: "4px",
              }}
            />
          )}
        />
      </div>

      {/* Action buttons similar to FilterDatePicker ashwin variant */}
      <div className="filter-range-picker-footer">
        <button 
          onClick={handleCancel}
          className="filter-cancel-btn"
        >
          Cancel
        </button>
        <button 
          onClick={handleApply}
          className="filter-apply-btn"
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default FilterRangePicker;
